import React, { useState, useRef, useEffect } from "react";

interface Option {
  label: string;
  value: string;
}

type ChangeHandler =
  | ((value: string) => void)
  | ((e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void);

interface CustomSelectProps {
  name?: string;
  value?: string;
  options: Option[];
  onChange: ChangeHandler;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

const DROPDOWN_HEIGHT = 240; 

const CustomSelect: React.FC<CustomSelectProps> = ({
  name,
  value,
  options,
  onChange,
  placeholder = "Select option",
  className = "",
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState<"top" | "bottom">("bottom");
  const ref = useRef<HTMLDivElement>(null);

  const selected = options.find((o) => o.value === value);

  const handleToggle = () => {
    if (disabled) return;

    if (!open && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      setPlacement(spaceBelow < DROPDOWN_HEIGHT && spaceAbove > spaceBelow ? "top" : "bottom");
    }

    setOpen((prev) => !prev);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (val: string) => {
    if (disabled) return;

    if (typeof onChange === "function") {
      const event = {
        target: { name, value: val },
      } as React.ChangeEvent<HTMLInputElement | HTMLSelectElement>;

      try {
        (onChange as (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void)(event);
      } catch {
        (onChange as (value: string) => void)(val);
      }
    }

    setOpen(false);
  };

  return (
    <div
      ref={ref}
      className={`relative w-full ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
    >
      {/* Trigger */}
      <div
        onClick={handleToggle}
        className={`border border-gray-300 rounded-md px-4 py-2 bg-white flex justify-between items-center cursor-pointer ${className}`}
      >
        <span className={selected ? "text-gray-800" : "text-gray-400"}>
          {selected ? selected.label : placeholder}
        </span>

        <svg
          className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {open && !disabled && (
        <div
          className={`absolute z-50 w-full bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto ${
            placement === "top" ? "bottom-full mb-1" : "top-full mt-1"
          }`}
        >
          {placeholder && (
            <div
              onClick={() => handleSelect("")}
              className="px-4 py-2 cursor-pointer text-gray-400 hover:bg-gray-100"
            >
              {placeholder}
            </div>
          )}

          {options.map((option) => (
            <div
              key={option.value}
              onClick={() => handleSelect(option.value)}
              className={`px-4 py-2 cursor-pointer hover:bg-blue-50 ${
                value === option.value ? "bg-blue-100 font-medium" : ""
              }`}
            >
              {option.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CustomSelect;